import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3, Table2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ComponentType, TrendSeries } from '../types';
import { CATEGORY_META, formatMoney, MASK } from '../lib/format';

interface AnalyticsChartProps {
  series?: TrendSeries;
  loading: boolean;
  error: boolean;
  revealed: boolean;
  months: number;
  onMonths: (m: number) => void;
  onRetry: () => void;
}

// Series order (left → right within each period group): net, deductions, employer.
const STACK: Array<{ key: string; labelKey: string; color: string }> = [
  { key: 'net', labelKey: 'myPay.netPay', color: '#16a34a' },
  { key: 'STATUTORY_TAX', labelKey: CATEGORY_META.STATUTORY_TAX.labelKey, color: CATEGORY_META.STATUTORY_TAX.color },
  { key: 'PRE_TAX_DEDUCTION', labelKey: CATEGORY_META.PRE_TAX_DEDUCTION.labelKey, color: CATEGORY_META.PRE_TAX_DEDUCTION.color },
  { key: 'POST_TAX_DEDUCTION', labelKey: CATEGORY_META.POST_TAX_DEDUCTION.labelKey, color: CATEGORY_META.POST_TAX_DEDUCTION.color },
  { key: 'EMPLOYER_CONTRIBUTION', labelKey: CATEGORY_META.EMPLOYER_CONTRIBUTION.labelKey, color: CATEGORY_META.EMPLOYER_CONTRIBUTION.color },
];

const PERIODS = [3, 6, 12];

const sumType = (series: TrendSeries | undefined, type: ComponentType) =>
  (series?.points ?? []).reduce((s, p) => s + (p.byType[type] ?? 0), 0);

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ series, loading, error, revealed, months, onMonths, onRetry }) => {
  const { t } = useTranslation('payroll');
  const [showTable, setShowTable] = useState(false);

  const cur = series?.currency ?? 'TZS';
  const { data, activeKeys, stats } = useMemo(() => {
    const present = new Set<string>(['net']);
    const rows = (series?.points ?? []).map((p) => {
      const row: Record<string, number | string> = { period: p.periodLabel, net: p.net, gross: p.gross };
      for (const [ty, v] of Object.entries(p.byType)) {
        if ((v ?? 0) !== 0) {
          row[ty] = v as number;
          present.add(ty);
        }
      }
      return row;
    });
    const pts = series?.points ?? [];
    const nets = pts.map((p) => p.net);
    return {
      data: rows,
      activeKeys: STACK.filter((s) => present.has(s.key)),
      stats:
        pts.length === 0
          ? null
          : {
              avgNet: nets.reduce((s, n) => s + n, 0) / nets.length,
              highestNet: Math.max(...nets),
              totalGross: pts.reduce((s, p) => s + p.gross, 0),
              totalTaxes: sumType(series, 'STATUTORY_TAX'),
            },
    };
  }, [series]);

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-h3 text-charcoal">
            <BarChart3 className="h-5 w-5 text-[#D9711E]" /> {t('myPay.payByPeriod')}
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border p-0.5">
              {PERIODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onMonths(m)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium',
                    months === m ? 'bg-charcoal text-white' : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  {m}M
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setShowTable((s) => !s)}>
              <Table2 className="h-3.5 w-3.5" /> {showTable ? t('myPay.showChart') : t('myPay.showTable')}
            </Button>
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <AlertCircle className="h-8 w-8 text-[#991B1B]" />
            <p className="text-charcoal">{t('myPay.trendError')}</p>
            <Button variant="outline" onClick={onRetry}>{t('myPay.retry')}</Button>
          </div>
        ) : data.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{t('myPay.trendEmpty')}</p>
        ) : (
          <>
            {stats && (
              <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <TrendStat label={t('myPay.avgNet')} value={revealed ? formatMoney(stats.avgNet, cur) : MASK} />
                <TrendStat label={t('myPay.highestNet')} value={revealed ? formatMoney(stats.highestNet, cur) : MASK} />
                <TrendStat label={t('myPay.totalGross')} value={revealed ? formatMoney(stats.totalGross, cur) : MASK} />
                <TrendStat label={t('myPay.totalTaxes')} value={revealed ? formatMoney(stats.totalTaxes, cur) : MASK} />
              </div>
            )}
            {showTable ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">{t('myPay.period')}</th>
                      <th className="py-2 pr-4 text-right font-medium">{t('myPay.gross')}</th>
                      <th className="py-2 text-right font-medium">{t('myPay.net')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((r, i) => (
                      <tr key={i} className="border-b border-border">
                        <td className="py-2 pr-4">{r.period}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{revealed ? formatMoney(r.gross as number, cur) : MASK}</td>
                        <td className="py-2 text-right tabular-nums">{revealed ? formatMoney(r.net as number, cur) : MASK}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }} barCategoryGap="24%" barGap={2} maxBarSize={16}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} tickMargin={8} />
                    <YAxis
                      tick={revealed ? { fontSize: 11 } : false}
                      width={revealed ? 56 : 8}
                      tickFormatter={(v) => formatMoney(v, cur, { compact: true })}
                    />
                    <Tooltip
                      formatter={(v: number, name: string) => [revealed ? formatMoney(v, cur) : MASK, name]}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {activeKeys.map((s) => (
                      <Bar key={s.key} dataKey={s.key} name={t(s.labelKey)} fill={s.color} radius={[2, 2, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                {!revealed && (
                  <p className="-mt-2 text-center text-[11px] text-muted-foreground">{t('myPay.maskedChartHint')}</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const TrendStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border border-border px-3 py-2">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-semibold tabular-nums text-charcoal">{value}</p>
  </div>
);
